import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Text } from 'rizzui';
import { toast } from 'sonner';
import { usePrivilege } from '../../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../../components/table/components/TableFilter';
import useFetch from '../../../../hooks/useFetch';
import useFilters from '../../../../hooks/useFilters';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../../router/menu';
import { CallsService } from '../../../../services/calls/callsService';
import { CallsRecordsApiResponse } from '../../../../type/entities/calls-records-type';
import CallProfileLayout from '../CallProfileLayout';
import CallRecordsFilters from './CallRecordsFilters';

interface CallRecordsProps { }

const columnOrder = [
    'callRecordId',
    'callId',
    'actionId',
    'url',
    'fileName',
    'receivedAt',
];

const CallRecords: React.FC<CallRecordsProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new CallsService();

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters({ client: id });

    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const downloadRecording = async (callRecordId: string, fileName: string) => {
        try {
            const response = await service.downloadCallRecord(callRecordId);
            const blob = new Blob([response.getResponse()?.data], { type: response.getResponse()?.headers?.['content-type'] || 'audio/mpeg' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (downloadError: any) {
            handleErrors(downloadError);
            toast.error('No se ha podido descargar la grabación.');
        }
    };

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (id && filters?.filter_filters?.call !== id) return null;
        const response = await service.listCallRecords(filters);
        return response.getResponseData() as CallsRecordsApiResponse;
    }, [id, filters]));

    useEffect(() => {
        updateFilters({ call: id });
    }, [id]);

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <CallProfileLayout>
            {data !== undefined && (
                <div className="pt-4">
                    <CustomTable
                        id={'calls-records-table'}
                        columnOrder={columnOrder}
                        data={orderedData()}
                        isLoading={loading}
                        columnsNotShown={['callRecordId', 'callId']}
                        overrideColumns={[
                            {
                                key: 'callId',
                                render: (row: any) => {
                                    return (
                                        userCan('list_calls', 'calls')
                                            ? (
                                                <Link to={`${menuRoutes.calls.path}/${row.row.original.callId}`}>
                                                    <Text className='font-bold text-primary'>Ir a la llamada</Text>
                                                </Link>
                                            )
                                            : <Text className='font-bold text-primary'>Ir a la llamada</Text>
                                    )
                                },
                            },
                            {
                                key: 'url',
                                render: (row: any) => {
                                    const url = row.row.original.url;
                                    const fileName = row.row.original.fileName || `grabacion_${row.row.original.actionId}.mp3`;

                                    if (!url) {
                                        return <Text color="gray">N/A</Text>;
                                    }

                                    return (
                                        <button
                                            type="button"
                                            onClick={() => downloadRecording(row.row.original.callRecordId, fileName)}
                                            className="font-bold text-primary"
                                        >
                                            Descargar grabación
                                        </button>
                                    );
                                },
                            }
                        ]}
                        actions={[]}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: (data as CallsRecordsApiResponse) ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    />
                </div>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Llamadas'}>
                <CallRecordsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </CallProfileLayout>
    );
};

export default CallRecords;
